import { useEffect, useRef, useState } from "react";
import classes from "../../PaymentMethods.module.scss";
import braintree, { ApplePay } from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import { useDonationFormStore } from "@/stores";
import { ORGANIZATION_NAME } from "@/config";
import { getAsset } from "@/utils/assets";
import { getCountryName } from "@/utils/countries";
import type { WalletButtonProps } from "@/payment/types";
import { useBraintree } from "./useBraintree";

const APPLE_PAY_VERSION = 3;

interface ApplePayValidateMerchantEvent {
  validationURL: string;
}

interface ApplePayPaymentAuthorizedEvent {
  payment: {
    token: unknown;
    shippingContact?: {
      emailAddress?: string;
    };
    billingContact?: {
      addressLines?: string;
      subLocality?: string;
      locality?: string;
      administrativeArea?: string;
      postalCode?: string;
      countryCode?: string;
      country?: string;
      givenName?: string;
      familyName?: string;
      email?: string;
    };
  };
}

function applePaySupported() {
  return (
    // @ts-expect-error: ApplePaySession supportsVersion is not recognized by TypeScript
    globalThis.ApplePaySession?.supportsVersion(APPLE_PAY_VERSION) &&
    // @ts-expect-error: ApplePaySession canMakePayments is not recognized by TypeScript
    globalThis.ApplePaySession?.canMakePayments()
  );
}

export function BraintreeApplePayButton(props: Readonly<WalletButtonProps>) {
  const { amount, onSuccess, onError } = props;
  const { braintreeClient } = useBraintree();

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [applePayInstance, setApplePayInstance] = useState<ApplePay>();
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);
  // ApplePaySession is a page-level singleton: the ref guards re-entry
  // synchronously (state updates are too late for a same-tick second click).
  const isProcessingRef = useRef(false);
  const {
    setGatewayPayload,
    setAddress,
    setIsManualAddress,
    setPersonalDetails,
  } = useDonationFormStore();

  useEffect(() => {
    if (!braintreeClient) return;
    const isSupported = applePaySupported();

    setIsApplePaySupported(isSupported);

    const initializeApplePay = async () => {
      try {
        const appleInstance = await braintree.applePay.create({
          client: braintreeClient,
        });
        setApplePayInstance(appleInstance);
      } catch (error) {
        trackError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isSupported) {
      initializeApplePay();
    }
  }, [braintreeClient]);

  const startProcessing = () => {
    isProcessingRef.current = true;
    setIsProcessing(true);
  };

  const finishProcessing = () => {
    isProcessingRef.current = false;
    setIsProcessing(false);
  };

  const handleApplePayment = async () => {
    if (!applePayInstance || isProcessingRef.current) return;

    startProcessing();

    try {
      const paymentRequest = applePayInstance.createPaymentRequest({
        total: {
          label: "Donation",
          amount: `${amount}`,
        },
        requiredShippingContactFields: ["email"],
        requiredBillingContactFields: ["postalAddress"],
      });

      // @ts-expect-error: ApplePaySession is not recognized by TypeScript
      const session = new globalThis.ApplePaySession(
        APPLE_PAY_VERSION,
        paymentRequest,
      );

      let closed = false;
      const finishSession = () => {
        // Late callbacks from a cancelled session must not unlock a retry.
        if (closed) return;
        closed = true;
        finishProcessing();
      };

      const completePayment = (status: number) => {
        if (closed) return false;
        try {
          session.completePayment(status);
          return true;
        } catch (error) {
          // Sentry also records the browser context automatically.
          trackError(error as Error, {
            payload: { context: "applePay.completePayment", status },
          });
          return false;
        } finally {
          finishSession();
        }
      };

      session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
        Promise.resolve()
          .then(() =>
            applePayInstance.performValidation({
              validationURL: event.validationURL,
              displayName: ORGANIZATION_NAME,
            }),
          )
          .then((merchantSession) => {
            if (closed) return;
            session.completeMerchantValidation(merchantSession);
          })
          .catch((error: Error) => {
            if (closed) return;
            trackError(error);
            try {
              session.abort();
            } catch (abortError) {
              trackError(abortError as Error);
            } finally {
              finishSession();
            }
          });
      };

      session.oncancel = finishSession;

      session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
        const payment = event.payment;

        Promise.resolve()
          .then(() => applePayInstance.tokenize({ token: payment.token }))
          .then((payload) => {
            if (closed) return;
            if (!payload.nonce) {
              throw new Error("No nonce found");
            }

            // Do not persist a wallet result or advance if its sheet was closed.
            // @ts-expect-error ApplePaySession STATUS constants
            if (!completePayment(globalThis.ApplePaySession.STATUS_SUCCESS)) {
              return;
            }

            setGatewayPayload({ nonce: payload.nonce });
            const email = payment.shippingContact?.emailAddress || "";

            const firstName = payment.billingContact?.givenName || "";
            const lastName = payment.billingContact?.familyName || "";

            setPersonalDetails({
              title: "",
              firstName,
              lastName,
              email,
              confirmEmail: email,
            });

            const lines = payment.billingContact?.addressLines || [];
            const addressLine1 = lines[0] || "";
            const addressLine2 = lines[1] || "";

            const city = payment.billingContact?.locality || "";
            const region = payment.billingContact?.administrativeArea || "";
            const postalCode = payment.billingContact?.postalCode || "";
            const country = getCountryName(payment.billingContact?.countryCode);

            setAddress({
              "address-line-1": addressLine1,
              "address-line-2": addressLine2,
              city,
              region,
              "postal-code": postalCode,
              country,
            });

            setIsManualAddress(true);
            onSuccess({ nonce: payload.nonce });
          })
          .catch((err: Error) => {
            // Preserve the original failure even if completion also throws.
            trackError(err, { payload: { context: "applePay.authorize" } });
            if (closed) return;
            completePayment(
              // @ts-expect-error ApplePaySession STATUS constants
              globalThis.ApplePaySession.STATUS_FAILURE,
            );
            onError?.(err);
          });
      };

      session.begin();
    } catch (error) {
      finishProcessing();

      if (error instanceof Error && error.name === "AbortError") return;

      trackError(error as Error);
      onError?.(error as Error);
    }
  };

  if (!isApplePaySupported) {
    return null;
  }

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handleApplePayment}
      disabled={isLoading || isProcessing}
    >
      <span>Donate with</span>
      <img
        src={getAsset("/images/apple-pay.svg")}
        className={classes.icon}
        alt="Apple Pay"
        width={85}
        height={35}
      />
    </button>
  );
}
